import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Home, Search, Users, HandCoins } from "lucide-react";

const money = (n) => `$${Number(n).toLocaleString()}`;
const STATUS_STYLES = {
  active: "bg-[#EAF2E8] text-[#2D5A27] border-[#cfe3cb]",
  visitor: "bg-[#FEF3C7] text-[#B45309] border-[#f5e0a3]",
  inactive: "bg-[#F3EFEA] text-[#7A8A78] border-[#E5E0D8]",
};

export default function Families() {
  const [families, setFamilies] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/families").then((r) => setFamilies(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () =>
      families
        .filter((f) => f.family_name.toLowerCase().includes(search.toLowerCase()))
        .map((f) => ({
          ...f,
          members: [...f.members].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
          ),
        }))
        .sort((a, b) => a.family_name.localeCompare(b.family_name, undefined, { sensitivity: "base" })),
    [families, search]
  );

  return (
    <div className="space-y-6" data-testid="families-page">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[#526050]">{families.length} family groups</p>
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A8A78]" />
          <Input data-testid="families-search" placeholder="Search families..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white h-11" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filtered.map((f) => (
          <Card key={f.family_name} className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid={`family-card-${f.family_name.replace(/ /g, '-')}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#2D5A27] flex items-center justify-center text-white"><Home className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-serif text-xl font-semibold text-[#1C241B]">{f.family_name}</h3>
                  <p className="text-xs text-[#7A8A78] flex items-center gap-1"><Users className="h-3 w-3" />{f.member_count} members</p>
                </div>
              </div>
              {f.total_giving !== undefined && (
              <div className="text-right">
                <p className="text-xs text-[#7A8A78] flex items-center gap-1 justify-end"><HandCoins className="h-3 w-3" />Combined giving</p>
                <p className="font-serif text-lg font-bold text-[#D97706]">{money(f.total_giving)}</p>
              </div>
              )}
            </div>
            <div className="mt-5 space-y-2">
              {f.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg bg-[#FBF9F5] px-3 py-2">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ backgroundColor: m.avatar_color }}>
                      {m.photo_path
                        ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/members/${m.id}/photo?v=${m.photo_path}`} alt="" className="h-full w-full object-cover" />
                        : <>{m.first_name?.[0]}{m.last_name?.[0]}</>}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1C241B]">{m.name}</p>
                      <p className="text-xs text-[#7A8A78]">{m.phone || m.email || "—"}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`capitalize text-xs ${STATUS_STYLES[m.status]}`}>{m.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
